import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore, useGroupStore } from '../../stores'
import { GroupList, GroupForm, MemberSelector } from '../../components/group'
import { Button, Input } from '../../components/common'
import { useToast } from '../../components/common/Toast'
import { groupService } from '../../services'
import type { Group, GroupInsert, GroupUpdate } from '../../types/database.types'

export function GroupsPage() {
  const { profile } = useAuthStore()
  const {
    groups,
    isLoading,
    selectedGroup,
    fetchGroups,
    fetchGroupById,
    createGroup,
    updateGroup,
    deleteGroup,
    addMembers,
    removeMember,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
  } = useGroupStore()
  const toast = useToast()

  const [showForm, setShowForm] = useState(false)
  const [editingGroup, setEditingGroup] = useState<Group | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showMemberModal, setShowMemberModal] = useState(false)
  const [managingGroup, setManagingGroup] = useState<Group | null>(null)

  const categories = groupService.getCategories()

  useEffect(() => {
    if (profile?.id) {
      fetchGroups(profile.id)
    }
  }, [profile?.id, fetchGroups, searchQuery, filters])

  // Load group details when managing members
  useEffect(() => {
    if (managingGroup) {
      fetchGroupById(managingGroup.id)
    }
  }, [managingGroup, fetchGroupById])

  const handleSubmit = async (formData: GroupInsert | GroupUpdate) => {
    setIsSubmitting(true)
    try {
      if (editingGroup) {
        await updateGroup(editingGroup.id, formData as GroupUpdate)
        toast.success('Group updated successfully')
      } else {
        await createGroup(formData as GroupInsert)
        toast.success('Group created successfully')
      }
      setShowForm(false)
      setEditingGroup(null)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to save group'
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (group: Group) => {
    if (
      !confirm(
        `Delete "${group.name}"? This will remove all members from the group.`
      )
    ) {
      return
    }
    try {
      await deleteGroup(group.id)
      toast.success('Group deleted')
    } catch (error) {
      toast.error('Failed to delete group')
    }
  }

  const handleEdit = (group: Group) => {
    setEditingGroup(group)
    setShowForm(true)
  }

  const handleManageMembers = (group: Group) => {
    setManagingGroup(group)
    setShowMemberModal(true)
  }

  const handleAddMembers = async (clientIds: string[]) => {
    if (!managingGroup) return
    setIsSubmitting(true)
    try {
      await addMembers(managingGroup.id, clientIds)
      toast.success(`Added ${clientIds.length} member${clientIds.length !== 1 ? 's' : ''}`)
      // Refresh the group to show updated members
      if (profile?.id) {
        fetchGroups(profile.id)
      }
    } catch (error) {
      toast.error('Failed to add members')
    } finally {
      setIsSubmitting(false)
      setShowMemberModal(false)
    }
  }

  const handleRemoveMember = async (clientId: string) => {
    if (!managingGroup) return
    try {
      await removeMember(managingGroup.id, clientId)
      toast.success('Member removed')
      // Refresh the group
      if (profile?.id) {
        fetchGroups(profile.id)
      }
    } catch (error) {
      toast.error('Failed to remove member')
    }
  }

  const canManageGroups = profile?.subscription_tier !== 'free'

  if (!canManageGroups) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <svg
          className="w-16 h-16 text-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Group Management Locked
        </h1>
        <p className="text-gray-600 mb-6">
          Upgrade to Basic or higher to create groups for bulk assignment.
        </p>
        <Link to="/dashboard/subscription">
          <Button>Upgrade Subscription</Button>
        </Link>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Groups</h1>
          <p className="mt-1 text-gray-600">
            Organize clients into groups for bulk program assignment
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>Create Group</Button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          value={filters.category || ''}
          onChange={(e) =>
            setFilters({ category: e.target.value || undefined })
          }
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All categories</option>
          {categories.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      {/* Group list */}
      <GroupList
        groups={groups}
        isLoading={isLoading}
        onEditGroup={handleEdit}
        onDeleteGroup={handleDelete}
        onManageMembers={handleManageMembers}
        emptyMessage={
          searchQuery
            ? 'No groups match your search'
            : 'No groups yet. Create your first group to organize clients!'
        }
      />

      {/* Create/Edit form modal */}
      {showForm && profile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setShowForm(false)
              setEditingGroup(null)
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {editingGroup ? 'Edit Group' : 'Create New Group'}
            </h2>
            <GroupForm
              group={editingGroup}
              providerId={profile.id}
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowForm(false)
                setEditingGroup(null)
              }}
              isLoading={isSubmitting}
            />
          </div>
        </div>
      )}

      {/* Member management modal */}
      {showMemberModal && managingGroup && profile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setShowMemberModal(false)
              setManagingGroup(null)
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {managingGroup.name} - Members
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              Add or remove clients from this group
            </p>

            {/* Current members list */}
            {selectedGroup && selectedGroup.members.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Current Members ({selectedGroup.members.length})
                </h3>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-40 overflow-y-auto">
                  {selectedGroup.members.map((member) => (
                    <div
                      key={member.id}
                      className="p-3 flex items-center justify-between"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {member.client.full_name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {member.client.email}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveMember(member.client_id)}
                        className="text-sm text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add members */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Add Members
              </h3>
              <MemberSelector
                providerId={profile.id}
                existingMemberIds={
                  selectedGroup?.members.map((m) => m.client_id) || []
                }
                onAddMembers={handleAddMembers}
                onCancel={() => {
                  setShowMemberModal(false)
                  setManagingGroup(null)
                }}
                isLoading={isSubmitting}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
